import { Logger } from "../../../../clients/logger";
import { RouterClient } from "../../../../clients/routerClient";
import { BaseWindow } from "./BaseWindow";
import { MOVE_REASON, WINDOWSTATE } from "../../../../common/constants";
import { adjustBoundsToBeOnMonitorAsync, Bounds } from "../../../../common/Monitors";
import { WindowIdentifier, WindowBounds } from "../types";
import { removeWindowReferenceFromActiveWorkspace } from "../../workspace/workspaceAdminAPI";
import { StandardErrorCallback, StandardPromise } from "../../../../types";

const mouseLocation = {
	x: 0,
	y: 0,
};

/**
 * NativeWindow
 */
class NativeWindow extends BaseWindow {
	loggerChannel: string;

	location: WindowBounds | null;

	key: string;

	saveOnBoundsChanged: boolean;

	movements: any[];

	launchedByApp!: boolean;

	lastLocation: WindowBounds | null;

	events: any;

	lastMinimized: number;

	lastRestored: number;

	executablePath: string | null;

	descriptor: string | null;

	spawnedByContainer: boolean;

	maximizing: boolean;

	restoreBounds: WindowBounds | null;

	windowDescriptor: any;

	windowIdentifier!: WindowIdentifier;

	opacity!: number;

	alwaysOnTop!: boolean;

	frame!: false;

	uuid!: string;

	/**
	 * @param {*} params
	 * @param {string} [params.uuid]
	 * @param {object} [params.location]
	 * @param {string} [params.launchedByApp]
	 *
	 */
	constructor(params) {
		if (!params.windowType) {
			if (params.params) {
				params = Object.assign(params, params.params);
			}
			params.windowType = null;
		}
		super(params);
		this.loggerChannel = `ExternalWindowWrapper.${this.windowName}`;
		this.type = "external";
		this.windowType = params.windowType ?? "NativeWindow";
		this.location = params.windowMsg ? params.windowMsg.location : null;
		this.windowState = WINDOWSTATE.NORMAL;
		this.key = params.windowMsg ? params.windowMsg.uuid : params.uuid;
		this.windowOptions = {
			nativeWindowHandle: this.key,
		};
		this.saveOnBoundsChanged = this.launchedByApp;
		this.movements = [];
		this.lastLocation = this.location;
		this.events = {};
		this.lastMinimized = 0;
		this.lastRestored = 0;
		this.executablePath = null;
		this.descriptor = null;
		this.spawnedByContainer = false;
		this.maximizing = false;
		this.restoreBounds = null;
		this.bindFunctions();
		RouterClient.addListener(
			`AssimilationService.bounds-change-request.${this.windowName}`,
			this._triggerBoundsChangedFromAssimilation
		);
	}

	removeListeners() {
		Logger.system.debug("removeListeners not implemented");
	}

	_setResizable() {
		Logger.system.error("_setResizable not implemented");
	}

	_triggerBoundsChangedFromAssimilation(err, response) {
		this.eventManager.trigger("bounds-change-request", response.data);
	}

	bindFunctions() {
		this._triggerBoundsChangedFromAssimilation = this._triggerBoundsChangedFromAssimilation.bind(this);
		this._setBounds = this._setBounds.bind(this);
		this.endMovementHandler = this.endMovementHandler.bind(this);
		this._getBoundsFromSystem = this._getBoundsFromSystem.bind(this);
		this._close = this._close.bind(this);
	}

	endMovementHandler(err, message) {
		if (err) {
			return Logger.system.error(err);
		}
		// Logger.system.log("end sent");
		if (!message || !message.data || !message.data.location) {
			return;
		}

		this.location = message.data.location;
		this.eventManager.trigger("bounds-changed", this.location);
	}

	_addEventListener(params, cb = Function.prototype) {
		const { eventName } = params;
		this.addEventListener(eventName);
		cb();
	}

	addEventListener(event, handler = Function.prototype) {
		super.addEventListener(event, handler);
		// this event will come in from assimilation. when it does, we trigger the bounds-changed event, which throws bounds-change-end to the rest of the system.
		if (event == "bounds-changed") {
			RouterClient.addListener(`${this.name}.endMovement`, this.endMovementHandler);
		}
	}

	_removeEventListener(params, cb = Function.prototype) {
		const { eventName } = params;
		this.removeEventListener(eventName);
		cb();
	}

	removeEventListener(event, handler = Function.prototype) {
		super.removeEventListener(event, handler);
		// this event will come in from assimilation. when it does, we trigger the bounds-changed event, which throws bounds-change-end to the rest of the system.
		if (event == "bounds-changed") {
			RouterClient.removeListener(`${this.name}.endMovement`, this.endMovementHandler);
		}
	}

	/**
	 * Every other kind of window has listeners for bounds change end that then save
	 * the window's new state. Because assimilated windows don't, we must override
	 * this function and call the custom save function.
	 *
	 */
	stopMove() {
		super.stopMove({});
		this._saveWindowOptions();
	}

	_setBounds(params, cb = Function.prototype) {
		if (typeof params === "function") {
			cb = params;
			params = null;
		}

		const { bounds } = params;

		const newLocationUnrounded = {
			left: Number(bounds.left),
			top: Number(bounds.top),
			width: bounds.width ? Number(bounds.width) : Number(bounds.right) - Number(bounds.left),
			height: bounds.height ? Number(bounds.height) : Number(bounds.bottom) - Number(bounds.top),
			right: bounds.right ? Number(bounds.right) : Number(bounds.left) + Number(bounds.width),
			bottom: bounds.bottom ? Number(bounds.bottom) : Number(bounds.top) + Number(bounds.height),
		};
		const newLocation = newLocationUnrounded;
		const addRoundedEntryToLocation = ([key, value]) => {
			newLocation[key] = Math.round(value);
		};
		Object.entries(newLocationUnrounded).forEach(addRoundedEntryToLocation);

		Logger.system.verbose("send move ", this);

		this.lastLocation = newLocation;
		this.location = newLocation;
		this.mergeBounds(bounds);
		super._setBounds(params);
		// if the _setBounds function was async, can't use super...
		const asyncHack = async () => {
			// The .net client has a window SizeChanged event handler where sends new bounds to Finsemble to keep consistent native window state.
			// It was done in that way because assimilation doesn't track a case when the window bounds are changed programmatically.
			// To prevent the updating bounds loop (track in .net - set in assimilation - track .net - etc.) .net client sends the notUpdateAssimilation flag.
			if (!bounds.notUpdateAssimilation) {
				await RouterClient.query("Assimilation.moveWindow", {
					name: this.windowName,
					key: this.key,
					location: newLocation,
				});
			}
			this.eventManager.trigger("bounds-changing", {
				...newLocation,
			});
			this._saveWindowOptions();
			cb();
		};
		asyncHack();
	}

	_hide(params, cb = Function.prototype) {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		super._hide(params, async (err, response) => {
			// Note:, there's no params.fromAssimilation here because hidden isn't an event caught from the OS.
			if (response.shouldContinue) {
				this.windowDescriptor.autoShow = false;
				await RouterClient.query("Assimilation.hideWindow", {
					name: this.windowName,
					key: this.key,
					location: this.location,
				});
				this.eventManager.trigger("hidden");
				RouterClient.transmit("Assimilation.saveWindow", {
					key: this.key,
					autoShow: false,
				});
			}
			cb();
		});
	}

	_focus(params, cb = Function.prototype) {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		super._focus(params, async (err, response) => {
			if (response.shouldContinue && params.fromAssimilation) {
				this.eventManager.trigger("focused");
			}
			cb();
		});
	}

	_minimize(params, cb = Function.prototype) {
		if (Date.now() - this.lastRestored < 50) return cb();
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		if (!params) params = {};
		const self = this;
		super._minimize(params, async () => {
			if (params.fromAssimilation) {
				if (self.windowState === WINDOWSTATE.MAXIMIZED) {
					self.windowState = WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED;
				} else if (self.windowState === WINDOWSTATE.NORMAL) {
					self.windowState = WINDOWSTATE.MINIMIZED_WAS_NORMAL;
				}
				self.eventManager.trigger("minimized");
			} else {
				await RouterClient.query("Assimilation.minimizeWindow", {
					name: self.windowName,
					key: self.key,
					location: self.location,
				});
				RouterClient.transmit("DockingService.wipeAutoArrange", {
					win: self.windowName,
				});
			}

			// if maximize is being called during the workspace load to restore state, then don't dirty workspace
			if (!params.initialLoad) {
				this._saveWindowOptions();
			}

			cb();
		});
	}

	_maximize(params, cb = Function.prototype) {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		const self = this;
		if (this.windowState === WINDOWSTATE.MAXIMIZED) return cb();
		super._maximize(params, async () => {
			if (self.windowState != WINDOWSTATE.MAXIMIZED && !params.fromAssimilation) {
				// self.emit("maximized", { name: self.windowName });
				if (
					self.windowState !== WINDOWSTATE.MINIMIZED_WAS_NORMAL &&
					self.windowState !== WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED
				) {
					self.maximizing = true;
					self.restoreBounds = self.location;
				}
				await RouterClient.query("Assimilation.maximizeWindow", {
					name: self.windowName,
					windowIdentifier: self.windowIdentifier,
				});
				RouterClient.transmit("DockingService.wipeAutoArrange", {
					win: self.windowName,
				});
				self.eventManager.trigger("maximized");
				self.windowState = WINDOWSTATE.MAXIMIZED;
			}

			// if maximize is being called during the workspace load to restore state, then don't dirty workspace
			if (!params.initialLoad) {
				this._saveWindowOptions();
			}

			cb();
		});
	}

	_getOptions(params, cb) {
		Logger.system.debug("externalWindowWrapper._getOptions", this.name, params);
		const opts = Object.assign({}, this.windowDescriptor);
		cb(null, opts);
	}

	private async setNormal(params: any) {
		if (params.fromAssimilation) {
			// Window restored from outside of Finsemble. If the monitor configuration has changed since the window was minimized, assimilation
			// will have moved the window's restore position. Use these bounds to tell Finsemble the new location.
			const onBoundsReceivedAfterRestore = (updatedBounds) => {
				// Assimilation corrects the windows position on restore, but html windows do not.
				// Due to this difference we need to tell finsemble to force the bounds update
				// or Finsemble will have the wrong bounds for this window. Only checking top and left
				// because the updated bound are coming in with 7 pixels of extra height even when no monitor change has occurred.
				if (this.location?.top !== updatedBounds.top || this.location?.left !== updatedBounds.left) {
					updatedBounds.force = true;
				}
				// Triggers an event that specified that the window was restored from the system
				updatedBounds.action = MOVE_REASON.SYSTEM_RESTORED;
				this.eventManager.trigger("system-bounds-changed", {
					...updatedBounds,
				});
			};

			const retryLimit = 3;
			let retryCount = 0;
			// The bounds request will return an error if the window is minimized or hidden. This can happen if the window
			// is not done restoring when we request the updated bounds. By retrying on error, we can give the OS more time to restore the window
			// Finally default to the previous location (adjusted to be on a monitor if necessary) if we can't updated bounds.
			const getBoundsAndRetry = () => {
				if (retryCount >= retryLimit) {
					Logger.system.error(
						`externalWindowWrapper._restore: Failed to get valid bounds for ${this.windowName} after trying ${retryLimit} times. Defaulting to previous location`
					);
					onBoundsReceivedAfterRestore(this.location);
					return;
				}
				this._getBoundsFromSystem({}).then((getBoundsResponse) => {
					if (getBoundsResponse.err) {
						retryCount++;
						setTimeout(() => {
							getBoundsAndRetry();
						}, retryCount * 100);
					} else {
						onBoundsReceivedAfterRestore(getBoundsResponse.data);
					}
				});
			};
			getBoundsAndRetry();
			this.eventManager.trigger("restored");
		} else {
			const left = this.location?.left ?? 0;
			const top = this.location?.top ?? 0;
			const width = this.location?.width ?? 0;
			const height = this.location?.height ?? 0;
			const right = this.location?.right ?? left + width;
			const bottom = this.location?.bottom ?? top + height;
			const bounds: Bounds = {
				right,
				left,
				top,
				bottom,
				width,
				height,
			};
			// Restore action was triggered from Finsemble. Make sure the window is restored on monitor.
			const adjustedBounds = await adjustBoundsToBeOnMonitorAsync(bounds);
			await RouterClient.query(
				"Assimilation.restoreWindow",
				{ name: this.windowName, key: this.key, location: adjustedBounds },
				params,
				() => {}
			);
			this.eventManager.trigger("restored");
		}
		this.windowState = WINDOWSTATE.NORMAL;
	}

	async _restore(
		params,
		cb?: StandardErrorCallback<{ shouldContinue: boolean }>
	): StandardPromise<{ shouldContinue: boolean }> {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		params = params || {};
		const { data: response } = await super._restore(params);
		if (response?.shouldContinue) {
			if (this.windowState === WINDOWSTATE.MINIMIZED_WAS_MAXIMIZED) {
				// Note:, early return
				// _maximize() is prevented if the fromAssimilation is true
				delete params.fromAssimilation;
				await this._maximize(params);
				this.eventManager.trigger("restored");
				cb?.(null, { shouldContinue: true });
				return { data: { shouldContinue: true } };
			}

			// if we restore from a minimized state set the state to normal
			if (this.windowState === WINDOWSTATE.MINIMIZED_WAS_NORMAL) {
				await this.setNormal(params);
			}

			// if we restore from a max state tell docking so we can reset the position
			if (this.windowState === WINDOWSTATE.MAXIMIZED) {
				this.windowState = WINDOWSTATE.NORMAL;
				this.maximizing = false;
				if (!params.fromAssimilation) {
					await RouterClient.query("Assimilation.restoreWindow", {
						name: this.windowName,
						windowIdentifier: this.windowIdentifier,
					});
				}
				this._getBoundsFromSystem({}, (err, bounds) => {
					this.eventManager.trigger("restored", {
						bounds,
						previousState: WINDOWSTATE.MAXIMIZED,
					});
				});
			}
		}

		this._saveWindowOptions();

		cb?.(null, { shouldContinue: true });
		return { data: { shouldContinue: true } };
	}

	_close(
		params: {
			fromSystem?: any;
			removeFromWorkspace?: any;
			stackedWindowIdentifier?: any;
			windowIdentifier?: any;
			noDocking?: any;
			force?: boolean;
		} = {},
		cb?: StandardErrorCallback<{ shouldContinue: boolean }>
	): StandardPromise<{ shouldContinue: boolean }> {
		return new Promise(async (resolve) => {
			if (typeof params === "function") {
				cb = params;
				params = {};
			}

			if (!params) params = {};
			RouterClient.removeListener(
				`AssimilationService.bounds-change-request.${this.windowName}`,
				this._triggerBoundsChangedFromAssimilation
			);
			const { err, data } = await super._close(params);
			if (data?.shouldContinue) {
				if (params.removeFromWorkspace)
					removeWindowReferenceFromActiveWorkspace({ name: this.windowName || "undefined-window-name" });
				// Tell assimilation to close the window.
				const { err: errAssimilation, response } = await RouterClient.query("Assimilation.closeWindow", {
					name: this.windowName,
					key: this.key,
					location: this.location,
					...params,
				});
				cb?.(errAssimilation, response?.data);
				resolve({ err: errAssimilation, data: response?.data });
			} else {
				cb?.(err, data);
				resolve({ err, data });
			}
		});
	}

	async _show(
		params: {
			windowIdentifier?: WindowIdentifier;
			shouldMaximize?: boolean;
			shouldRestore?: boolean;
			saveState?: boolean;
			switchedMonitors?: boolean;
		},
		cb?: StandardErrorCallback<{ shouldContinue: boolean }>
	): StandardPromise<{ shouldContinue: boolean }> {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		const { err, data } = await super._show(params);
		if (data?.shouldContinue) {
			this.windowDescriptor.autoShow = true;
			await RouterClient.query("Assimilation.showWindow", {
				name: this.windowName,
				key: this.key,
				location: self.location,
			});
			this.eventManager.trigger("shown");
			RouterClient.transmit("Assimilation.saveWindow", {
				key: this.key,
				autoShow: true,
			});
			this._saveWindowOptions();
		}
		cb?.(err, data);
		return { err, data };
	}

	_showAt(position, params, cb = Function.prototype) {
		Logger.system.debug("externalWindowWrapper._showAt", this.name, position, params);
		if (typeof params === "function") {
			cb = params;
			params = {};
		}

		super._showAt(params, async (err, response) => {
			cb(err, response);
		});
	}

	_bringToFront(params, cb = Function.prototype) {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		super._bringToFront(params, async (err, response) => {
			if (response.shouldContinue) {
				await RouterClient.query("Assimilation.bringToFront", {
					name: this.windowName,
				});
				this.eventManager.trigger("broughtToFront");
			}
			cb();
		});
	}

	async _setOpacity(params, cb = Function.prototype) {
		this.opacity = params.opacity;
		cb();
	}

	async _alwaysOnTop(params, cb = Function.prototype) {
		if (this.alwaysOnTop == params.alwaysOnTop) return cb();
		this.alwaysOnTop = params.alwaysOnTop;
		await RouterClient.query("Assimilation.alwaysOnTop", {
			name: this.windowName,
			alwaysonTop: params.alwaysOnTop,
		}); // the spelling here is intentional
		this.eventManager.trigger("alwaysOnTop", { alwaysOnTop: this.alwaysOnTop });
		cb();
	}

	/**
	 *
	 * Returns true if the window is always on top. False otherwise.
	 *
	 * Disable eslint below because params is undefined, but we don't want to refactor the other calls to expect the 1st param to equal the callback.
	 */
	_isAlwaysOnTop(params = {}, callback = Function.prototype) {
		if (typeof params === "function") {
			callback = params;
		}

		if (typeof this.alwaysOnTop !== "boolean" && this.windowDescriptor) {
			this.alwaysOnTop = this.windowDescriptor.alwaysOnTop;
		}

		callback(null, Boolean(this.alwaysOnTop));
		return Boolean(this.alwaysOnTop);
	}

	/**
	 * Returns true if the window is visible. False otherwise.
	 * @param {*} params Currently unused
	 * @param {*} callback calls back with true if visible, false if not.
	 */
	_isShowing(params = {}, callback = Function.prototype) {
		if (typeof params === "function") {
			callback = params;
		}

		// autoShow is false if the window is spawned hidden. It it updated every time the window is shown or hidden.
		const isVisible = !(this.windowDescriptor && this.windowDescriptor.autoShow === false);
		callback(null, isVisible);
		return isVisible;
	}

	disableFrame(cb = Function.prototype) {
		this.frame = false; // paint
		cb();
	}

	getMousePosition(cb = Function.prototype) {
		cb(null, mouseLocation);
	}

	_saveWindowOptions() {
		Logger.system.debug(
			`_saveWindowOptions Window id ${this.key}`,
			"location",
			this.location,
			"windowState",
			this.windowState.toString(),
			"restoreBounds",
			this.restoreBounds
		);
		RouterClient.transmit("Assimilation.saveWindow", {
			key: this.key,
			location: this.location,
			windowState: this.windowState,
			cachedBounds: this.restoreBounds,
		});
	}

	//  * Sends a request to assimilation for the system bounds. Waits and returns the response
	//  * @param {*} params
	//  * @param {*} cb
	//  */
	_getBoundsFromSystem(params, cb = Function.prototype): Promise<{ err: string; data: WindowBounds }> {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}

		const self = this;
		const promiseResolver = (resolve) => {
			RouterClient.query(
				"Assimilation.getWindowLocation",
				{ windowName: this.windowName, force: true },
				(err, message) => {
					if (err) {
						cb(err);
						return resolve({ err, data: null });
					}
					if (!message.data.location) {
						cb(null);
						return resolve({ err: null, data: null });
					}
					self.location = {
						top: message.data.location.top,
						left: message.data.location.left,
						right: message.data.location.right,
						bottom: message.data.location.bottom,
						height: message.data.location.height,
						width: message.data.location.width,
					};

					cb(null, self.location);
					return resolve({ err: null, data: self.location });
				}
			);
		};
		return new Promise(promiseResolver);
	}

	_getBounds(params: { windowIdentifier?: any }, cb?: StandardErrorCallback<any>): StandardPromise<any> {
		if (typeof params === "function") {
			cb = params;
			params = {};
		}
		if (this.maximizing) {
			cb?.(null, this.restoreBounds);
			return Promise.resolve({ err: null, data: this.restoreBounds });
		}
		const promiseResolver = (resolve) => {
			RouterClient.query("Assimilation.getWindowLocation", { windowName: this.windowName }, (err, message) => {
				if (err) {
					cb?.(err);
					resolve({ err, data: null });
					return;
				}
				if (!message.data.location) {
					cb?.(null);
					resolve({ err: null, data: null });
					return;
				}
				this.location = {
					top: message.data.location.top,
					left: message.data.location.left,
					right: message.data.location.right,
					bottom: message.data.location.bottom,
					height: message.data.location.bottom - message.data.location.top,
					width: message.data.location.right - message.data.location.left,
				};
				if (!this.location.height) {
					this.location.height = (this.location.bottom ?? 0) - this.location.top;
				}
				if (!this.location.width) {
					this.location.width = (this.location.right ?? 0) - this.location.left;
				}
				cb?.(null, this.location);
				return resolve({ err: null, data: this.location });
			});
		};
		return new Promise(promiseResolver);
	}

	_updateOptions(params, cb = Function.prototype) {
		const windowOptions = {
			key: this.key,
		};

		if (params && typeof params === "object") {
			Object.keys(params).forEach((key) => {
				if (!windowOptions[key]) {
					windowOptions[key] = params[key];
				}
				if (key === "alwaysOnTop" && this.alwaysOnTop !== params[key]) {
					this.alwaysOnTop = params[key];
					this.eventManager.trigger("alwaysOnTop", {
						alwaysOnTop: this.alwaysOnTop,
					});
				}
			});
		}

		RouterClient.transmit("Assimilation.saveWindow", windowOptions);
		cb();
	}
}

export default NativeWindow;
